import React, { useContext, useState } from "react";
import { Context } from "../Store";
import styles from "./Form.module.scss";
import ReactGA from "react-ga4";
import { getStoriesAzureAPI } from "../utils/getStoriesAzureApi";
import { isMobile } from "react-device-detect";

const MAX_TIKTOK_HANDLE_LENGTH = 24;
const MAX_INSTAGRAM_HANDLE_LENGTH = 30;

const AnimText = ({ text }) => (
  <span className="btnText">
    <span>{text}</span>
  </span>
);

const Form = () => {
  const [state] = useContext(Context);
  const [step, setStep] = useState("promo"); // "promo" | "form" | "success"
  const [instagramProfile, setInstagramProfile] = useState("");
  const [tiktokProfile, setTiktokProfile] = useState("");
  const [status, setStatus] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { StoriesAzureAPI } = getStoriesAzureAPI(null);

  let trackEvent = (category, action, label = "") => {
    ReactGA.event({ category, action, label });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setStatus("");

    const trimmedInstagram = instagramProfile.trim();
    const trimmedTiktok = tiktokProfile.trim();

    if (!trimmedInstagram && !trimmedTiktok) {
      setStatus("validation");
      return;
    }

    if (trimmedTiktok.length > MAX_TIKTOK_HANDLE_LENGTH || trimmedInstagram.length > MAX_INSTAGRAM_HANDLE_LENGTH) {
      setStatus("length");
      return;
    }

    setIsSubmitting(true);
    trackEvent("Social Form", "Submit Clicked");

    try {
      const payload = {
        StoryId: state.guid,
        BookingReference: state.reference,
        InstagramProfile: trimmedInstagram,
        TiktokProfile: trimmedTiktok,
      };

      await StoriesAzureAPI.post("https://flightclubapi.azure-api.net/uk/v2/SubmitSocialMedia", payload);
      setStep("success");
      setInstagramProfile("");
      setTiktokProfile("");
    } catch (error) {
      setStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.innerWrapper}>
        <div className={styles.imageContainer}>
          {isMobile ? (
            <img
              src="./img/mob-air-balloons.png"
              alt="red and blue hot air balloons"
              className={styles.mobileBalloons}
            />
          ) : (
            <>
              <img src="./img/air-balloon-red.png" alt="red hot air balloon" className={styles.redBalloon} />
              <img src="./img/air-balloon-blue.png" alt="blue hot air balloon" className={styles.blueBalloon} />
            </>
          )}
        </div>

        {step === "promo" && (
          <>
            <div className={styles.heading}>
              <h4>Got something</h4>
              <h4>brilliant?</h4>
            </div>
            <p>
              Share your epic moments with us. Your killer throws, victory dances, reactions, and photobombs for a
              chance to be featured on our site and socials!
            </p>
            {state.country === "uk" && (
              <div className={`${styles.shareLinkWrapper} shareLinkWrapper`}>
                <button
                  className="shareLink"
                  onClick={() => {
                    setStep("form");
                    trackEvent("Social Form", "Share Button Clicked");
                  }}
                >
                  <AnimText text="Share with us" />
                </button>
              </div>
            )}
          </>
        )}

        {step === "form" && (
          <div className={styles.form}>
            <h4>Share your socials</h4>
            <p>
              Want to be tagged? Drop your TikTok and/or Instagram handles below, and we'll be in touch before posting.
              Please note that content may also be featured on our website.
            </p>
            <form className={styles.formFields} onSubmit={handleSubmit}>
              <label htmlFor="tiktokProfile">TikTok handle</label>
              <input
                type="text"
                id="tiktokProfile"
                name="tiktokProfile"
                value={tiktokProfile}
                onChange={(e) => setTiktokProfile(e.target.value)}
                maxLength={MAX_TIKTOK_HANDLE_LENGTH}
              />
              <label htmlFor="instagramProfile">Instagram handle</label>
              <input
                type="text"
                id="instagramProfile"
                name="instagramProfile"
                value={instagramProfile}
                onChange={(e) => setInstagramProfile(e.target.value)}
                maxLength={MAX_INSTAGRAM_HANDLE_LENGTH}
              />
              {status === "validation" && <div className={styles.error}>Please enter at least one social handle.</div>}
              {status === "length" && (
                <div className={styles.error}>TikTok can be max 24 characters and Instagram max 30 characters.</div>
              )}
              {status === "error" && <div className={styles.error}>Something went wrong. Please try again.</div>}
              <div className={`${styles.shareLinkWrapper} shareLinkWrapper`}>
                <button
                  className="shareLink"
                  type="submit"
                  disabled={isSubmitting}
                  onClick={() => {
                    setStep("form");
                    trackEvent("Social Form", "Share Button Clicked");
                  }}
                >
                  <AnimText text={isSubmitting ? "Submitting..." : "Submit"} />
                </button>
              </div>
            </form>
          </div>
        )}

        {step === "success" && (
          <>
            <h4>Thanks for sharing!</h4>
            <p style={{ textAlign: "center", padding: "20px 40px" }}>Your details have been submitted.</p>
          </>
        )}
      </div>
    </div>
  );
};

export default Form;
